
import { useState } from 'react';
import { AttendanceStatus, AttendanceRecord, useAttendanceStore } from '../store/attendanceStore';
import { useAttendanceData } from './attendance/useAttendanceData';
import { useAttendanceActions } from './attendance/useAttendanceActions';
import { useAttendanceStats } from './attendance/useAttendanceStats';
import { useTimetableStore } from '../store/timetableStore';
import { toast } from 'sonner';
import { format } from 'date-fns';

export const useTeacherAttendance = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { 
    selectedDate, 
    setSelectedDate,
    getTeachersWithAttendance 
  } = useAttendanceData();
  
  const { syncAttendanceWithTimetable } = useAttendanceActions();
  const { getAttendanceStats } = useAttendanceStats();
  
  const { 
    markAttendance, 
    updateAttendance,
    assignSubstitute,
    getTeacherAttendance,
    attendanceRecords 
  } = useAttendanceStore();
  
  const { timetableData } = useTimetableStore();

  const formattedDate = format(selectedDate, 'yyyy-MM-dd');

  // Keep existing functions with same implementation but using the new hooks
  const markTeacherAttendance = async (
    teacherId: string, 
    teacherName: string, 
    status: AttendanceStatus, 
    reason?: string
  ) => {
    setIsLoading(true);
    try {
      markAttendance({
        teacherId,
        teacherName,
        date: formattedDate,
        status,
        reason,
        checkInTime: new Date().toISOString(),
      });
      
      await syncAttendanceWithTimetable(teacherId, status, timetableData);
      
      toast.success(`${teacherName} marked as ${status}`);
      return true;
    } catch (error) {
      console.error('Failed to mark attendance:', error);
      toast.error('Failed to update attendance');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const updateTeacherAttendance = async (
    attendanceId: string,
    updates: Partial<AttendanceRecord>
  ) => {
    setIsLoading(true);
    try {
      updateAttendance(attendanceId, updates);
      
      if (updates.status) {
        const record = attendanceRecords.find(r => r.id === attendanceId);
        if (record) {
          await syncAttendanceWithTimetable(record.teacherId, updates.status, timetableData);
        }
      }
      
      toast.success("Attendance updated");
      return true;
    } catch (error) {
      console.error('Error updating attendance:', error);
      toast.error('Failed to update attendance');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const assignSubstituteTeacher = (
    attendanceId: string,
    substituteId: string, 
    substituteName: string
  ) => {
    try {
      assignSubstitute(attendanceId, substituteId, substituteName);
      toast.success(`${substituteName} assigned as substitute`);
      return true;
    } catch (error) {
      console.error('Error assigning substitute:', error);
      toast.error('Failed to assign substitute');
      return false;
    }
  };

  return {
    isLoading,
    selectedDate,
    setSelectedDate,
    markTeacherAttendance,
    updateTeacherAttendance,
    assignSubstituteTeacher,
    getTeachersWithAttendance: () => getTeachersWithAttendance(timetableData),
    getAttendanceStats,
    getTeacherAttendance
  };
};
